import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Info from './Info';
import LanguageSelector from './LanguageSelector';
import { useAdminLanguage } from './AdminI18n';
import { adminFetch } from './adminApi';
import useTableSort from './useTableSort';

function RegisterAdmin() {
  const { t } = useAdminLanguage();
  const [registers, setRegisters] = useState([]);
  const [formData, setFormData] = useState({ registre: '', abreviation: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [language, setLanguage] = useState('fr');

  useEffect(() => {
    fetchRegisters();
  }, [language]);

  const fetchRegisters = async () => {
    try {
      const response = await adminFetch(`/api/registers?lang=${language}`);
      const data = await response.json();
      setRegisters(data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des registres');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.registre.trim() || !formData.abreviation.trim()) {
        setError('Tous les champs sont obligatoires');
        return;
      }

      setLoading(true);
      setError('');
      setSuccess('');

      if (isEditing) {
        await adminFetch(`/api/registers/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, lang: language }),
        });
        setSuccess('Registre mis à jour avec succès');
      } else {
        await adminFetch('/api/registers', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, lang: language }),
        });
        setSuccess('Registre ajouté avec succès');
      }

      setFormData({ registre: '', abreviation: '' });
      setIsEditing(false);
      setEditingId(null);
      fetchRegisters();
    } catch (err) {
      setError('Erreur lors de l\'enregistrement du registre');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (register) => {
    setIsEditing(true);
    setEditingId(register.id);
    setFormData({ registre: register.registre, abreviation: register.abreviation });
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await adminFetch(`/api/registers/${id}?lang=${language}`, {
        method: 'DELETE',
      });
      setSuccess('Registre supprimé avec succès');
      fetchRegisters();
    } catch (err) {
      setError('Erreur lors de la suppression du registre');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => ([
    { id: 'registre', label: t('Registre'), sortable: true },
    { id: 'abreviation', label: t('Abréviation'), sortable: true },
  ]), [t]);

  const { order, orderBy, sortedRows, handleRequestSort } = useTableSort(registers, {
    defaultOrderBy: 'registre',
  });

  const renderCellContent = (register, columnId) => register[columnId];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des registres')}
          </Typography>

          <Info type="register" />

          <LanguageSelector 
            language={language} 
            onLanguageChange={setLanguage} 
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(error)}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t(success)}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t(isEditing ? 'Modifier le registre' : 'Ajouter un registre')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="registre"
                  label={t('Registre')}
                  autoComplete="off"
                  value={formData.registre}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="abreviation"
                  label={t('Abréviation')}
                  autoComplete="off"
                  value={formData.abreviation}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? t('Enregistrement...') : t(isEditing ? 'Modifier' : 'Ajouter')}
                </Button>
                {isEditing && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setIsEditing(false);
                      setEditingId(null);
                      setFormData({ registre: '', abreviation: '' });
                    }}
                    sx={{ ml: 2 }}
                  >
                    {t('Annuler')}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              {t('Liste des registres')}
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sortDirection={orderBy === column.id ? order : false}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={() => handleRequestSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                    <TableCell>{t('Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((register) => (
                    <TableRow key={register.id}>
                      {columns.map((column) => (
                        <TableCell key={`${register.id}-${column.id}`}>
                          {renderCellContent(register, column.id)}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(register)}
                            color="primary"
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(register.id)}
                            color="error"
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Fade>
    </Container>
  );
}

export default RegisterAdmin;

